import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Award, FileCheck2 } from "lucide-react";

const sessions = [
  {
    num: "01",
    title: "Discover Your CFO Gap",
    theme: "Where are you today?",
    topics: "CFO Readiness Assessment",
    detail:
      "A full assessment of your leadership, technical accounting, FP&A, cash-flow, and board-communication readiness — pinpointing exactly what separates your current position from a CFO position.",
    deliverable: "CFO Readiness Scorecard + personal development roadmap",
  },
  {
    num: "02",
    title: "Think Like a CFO",
    theme: "Move beyond reporting the numbers",
    topics: "Strategy · FP&A · Forecasting · KPIs",
    detail:
      "Learn to explain what happened, why it happened, what will happen next — and what management should do about it. Budgets, rolling forecasts, variance analysis, and turning numbers into recommendations.",
    deliverable: "CFO Financial Analysis Toolkit — budget, cash forecast, KPI dashboard & variance templates",
  },
  {
    num: "03",
    title: "Operate Like a CFO",
    theme: "Build a finance department a CEO can trust",
    topics: "Controls · Risk · Accounting · Compliance",
    detail:
      "Month-end close, audit readiness, internal controls, and risk — practiced through real CFO case studies drawn from remediating reconciliation backlogs and restoring audit readiness.",
    deliverable: "CFO Control Framework — month-end & control checklists, risk register, SOPs",
  },
  {
    num: "04",
    title: "Lead Like a CFO",
    theme: "Stop speaking accounting. Start speaking business.",
    topics: "CEO · Board · Teams · Executive Communication",
    detail:
      "Board presentations, CEO conversations, and executive storytelling — including a live simulation where you present a cash-reserve warning and get challenged exactly as a CEO would.",
    deliverable: "CFO Executive Communication Toolkit — monthly CFO report, board dashboard, CEO briefing",
  },
  {
    num: "05",
    title: "Become the CFO",
    theme: "Turn CFO capability into a CFO opportunity",
    topics: "Career Positioning · Interviews · 90-Day Plan",
    detail:
      "CFO resume and LinkedIn positioning, compensation negotiation, recruiter strategy, and a full CFO interview simulation with a mentor who has held the seat.",
    deliverable: "CFO Career Launch Package — interview question bank + 30-60-90 day CFO plan",
  },
];

const TIER_VALUE = "Career Coaching — 5-Session Signature Program ($4,989)";

export default function CoachingJourney({ showPageLink = true }) {
  const selectTier = () => {
    sessionStorage.setItem("bc_selected_tier", TIER_VALUE);
    window.dispatchEvent(new CustomEvent("bc-tier-selected", { detail: TIER_VALUE }));
  };

  return (
    <section id="coaching-journey" data-testid="coaching-journey-section" className="border-t border-linen bg-white/40">
      <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
        >
          The CFO Accelerator&trade; · 5-Session Signature Program
        </motion.p>
        <div className="mb-20 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
            data-testid="coaching-journey-heading"
          >
            From Controller to <span className="italic text-forest">Strategic CFO</span>.
          </motion.h2>
          <p className="max-w-sm text-sm leading-relaxed text-pine/60">
            Learn how to think, operate, communicate, and lead at the CFO level — coached by a
            mentor who has run finance for organizations up to $46M.
          </p>
        </div>

        <div className="mx-auto max-w-3xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-4 border border-pine/20 bg-white px-8 py-5 text-center"
            data-testid="journey-start"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-pine/50">You arrive as</p>
            <p className="font-serif text-2xl font-semibold text-pine">Accountant / Controller</p>
          </motion.div>

          {sessions.map((s, i) => (
            <div key={s.num}>
              <div className="flex justify-center py-1 text-terra">
                <ArrowDown size={18} />
              </div>
              <motion.article
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                className="group border border-linen bg-white p-8 transition-colors duration-300 hover:border-forest/40 md:p-10"
                data-testid={`journey-session-${i + 1}`}
              >
                <div className="flex flex-col gap-6 md:flex-row md:gap-10">
                  <div className="shrink-0">
                    <p className="font-serif text-6xl font-semibold leading-none text-linen transition-colors duration-300 group-hover:text-terra/40">{s.num}</p>
                    <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-terra">Session</p>
                  </div>
                  <div>
                    <h3 className="mb-1 font-serif text-2xl font-semibold tracking-tight text-pine">{s.title}</h3>
                    <p className="mb-1 font-serif text-base italic text-forest">{s.theme}</p>
                    <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.2em] text-pine/50">{s.topics}</p>
                    <p className="mb-5 text-sm leading-relaxed text-pine/70">{s.detail}</p>
                    <p className="flex items-start gap-2 border-l-2 border-terra pl-4 text-sm text-pine/80">
                      <FileCheck2 size={15} className="mt-0.5 shrink-0 text-terra" />
                      <span><span className="font-semibold">You receive:</span> {s.deliverable}</span>
                    </p>
                  </div>
                </div>
              </motion.article>
            </div>
          ))}

          <div className="flex justify-center py-1 text-terra">
            <ArrowDown size={18} />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="border-2 border-forest bg-forest px-8 py-8 text-center text-bone"
            data-testid="journey-end"
          >
            <Award size={28} className="mx-auto mb-3 text-terra" />
            <p className="mb-1 font-serif text-3xl font-semibold tracking-tight">CFO Ready</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/70">
              Think strategically → Lead financially → Communicate executively → Get the CFO role
            </p>
          </motion.div>

          <div className="mt-10 text-center">
            <a
              href="#booking"
              onClick={selectTier}
              data-testid="journey-cta"
              className="inline-flex items-center gap-2 border border-forest bg-forest px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest"
            >
              Begin the Accelerator — $4,989 <ArrowRight size={16} />
            </a>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-pine/40">
              Or start with a single $200 session — book a free consultation to decide
            </p>
            {showPageLink && (
              <a
                href="/cfo-accelerator"
                data-testid="journey-full-page-link"
                className="mt-6 inline-block font-mono text-xs uppercase tracking-[0.2em] text-forest underline-offset-4 hover:underline"
              >
                Explore the full program: who it's for, our promise & more →
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
